import { useAtomValue, useSetAtom } from 'jotai';
import { SidePanel } from '@carbon/ibm-products';
import 'components/inline-edit/ibm-products-config';
import {
  CodeSnippet,
  DefinitionTooltip,
  InlineLoading,
  InlineNotification,
} from '@carbon/react';
import { num } from './format';
import { promptTextAtom, selectionAtom, loadableErrorMessage } from './atoms';
import styles from './page.module.scss';

export function ViewPromptPanel() {
  const selection = useAtomValue(selectionAtom);
  const setSelection = useSetAtom(selectionAtom);
  const textState = useAtomValue(promptTextAtom);

  const loading = textState.state === 'loading';
  const error = loadableErrorMessage(textState, 'Failed to load prompt');
  const prompt =
    textState.state === 'hasData' ? textState.data?.prompt : undefined;

  return (
    <SidePanel
      open={selection !== null}
      onRequestClose={() => setSelection(null)}
      title={selection?.title ?? ''}
      subtitle={prompt ? `Version ${prompt.version}` : ''}
      placement="right"
      size="lg"
      slideIn
      selectorPageContent="#main-content"
    >
      {loading && <InlineLoading description="Loading prompt…" />}
      {error && (
        <InlineNotification
          kind="error"
          title="Failed to load prompt"
          subtitle={error}
          lowContrast
        />
      )}
      {prompt && (
        <div className={styles.panelBody}>
          <div className={styles.keyDetails}>
            <div>
              <span className={styles.keyLabel}>
                <DefinitionTooltip
                  definition="Estimated from the stored template text (~4 chars/token). Excludes sections injected at request time."
                  openOnHover
                >
                  Est. Tokens
                </DefinitionTooltip>
              </span>
              <span>{num(prompt.estTokens)}</span>
            </div>
            <div>
              <span className={styles.keyLabel}>Documents Processed</span>
              <span>{num(prompt.documentsProcessed)}</span>
            </div>
          </div>
          {/* 0 rows disables Carbon's collapse: no "Show more", no inline
              max-height — the full prompt renders and scrolls with the panel. */}
          <CodeSnippet
            type="multi"
            feedback="Copied!"
            maxCollapsedNumberOfRows={0}
          >
            {prompt.text}
          </CodeSnippet>
        </div>
      )}
      {/* Carbon SidePanel requires non-null children even when closed/empty. */}
      {!prompt && !loading && !error && <span aria-hidden />}
    </SidePanel>
  );
}
